/**
 * useChangeSet.ts — the unit of work.
 *
 * Nothing in this workbench edits a project directly. Every change accumulates
 * into a change set (§8), is previewed by the Engine, and reaches Git only when
 * someone approves it and someone else publishes it. That is not a UI
 * convention: the server refuses any other order.
 *
 * The hook exists so no component has to remember the sequence. A component
 * asks for `preview(operations)` or `apply(operations)`; the change set is
 * created on first use, the base diagram revision travels with every request,
 * and a conflict comes back as a value the caller renders rather than an
 * exception it has to catch.
 */

import { useCallback, useState } from "react";
import { useClient } from "../../api/ClientContext";
import { ApiError } from "../../api/errors";
import type { AuthoringOperationInput, AuthoringPreview, AuthoringResult, ChangeSet } from "../../types/contract";

export interface ConflictReport {
  /** What the client believed, and what the server actually holds. */
  resource?: string;
  expectedRevision?: unknown;
  currentRevision?: unknown;
  changedBy?: unknown;
  changedAt?: unknown;
  message: string;
  requestId?: string;
}

export interface ChangeSetController {
  changeSet: ChangeSet | undefined;
  busy: boolean;
  preview: AuthoringPreview | undefined;
  conflict: ConflictReport | undefined;
  error: unknown;

  /** Create (or adopt) the draft this session authors into. */
  ensure: (title: string) => Promise<ChangeSet>;
  adopt: (changeSet: ChangeSet) => void;
  clearPreview: () => void;
  clearError: () => void;

  /** §12 — ask the Engine what it WOULD do. Nothing is written. */
  previewOperations: (operations: AuthoringOperationInput[], base?: { diagramId: string; revision: number }) => Promise<AuthoringPreview | undefined>;
  applyOperations: (operations: AuthoringOperationInput[], base?: { diagramId: string; revision: number }) => Promise<AuthoringResult | undefined>;

  submit: () => Promise<ChangeSet | undefined>;
  approve: () => Promise<ChangeSet | undefined>;
  requestChanges: (note: string) => Promise<ChangeSet | undefined>;
  publish: (message?: string) => Promise<ChangeSet | undefined>;
  abandon: () => Promise<ChangeSet | undefined>;
}

export function useChangeSet(projectId: string): ChangeSetController {
  const client = useClient();
  const [changeSet, setChangeSet] = useState<ChangeSet | undefined>(undefined);
  const [preview, setPreview] = useState<AuthoringPreview | undefined>(undefined);
  const [conflict, setConflict] = useState<ConflictReport | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [busy, setBusy] = useState(false);

  /**
   * Run a server call, separating the three outcomes that need different UI:
   * a result, a CONFLICT (someone else moved), and everything else.
   */
  const run = useCallback(async <T,>(action: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(undefined);
    setConflict(undefined);
    try {
      return await action();
    } catch (cause) {
      if (cause instanceof ApiError && cause.isConflict) {
        const details = (cause.details ?? {}) as Record<string, unknown>;
        setConflict({
          message: cause.message,
          ...(typeof details.resource === "string" ? { resource: details.resource } : {}),
          ...(details.expectedRevision !== undefined ? { expectedRevision: details.expectedRevision } : {}),
          ...(details.currentRevision !== undefined ? { currentRevision: details.currentRevision } : {}),
          ...(details.changedBy !== undefined ? { changedBy: details.changedBy } : {}),
          ...(details.changedAt !== undefined ? { changedAt: details.changedAt } : {}),
          ...(cause.requestId ? { requestId: cause.requestId } : {}),
        });
        return undefined;
      }
      setError(cause);
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);

  const ensure = useCallback(async (title: string): Promise<ChangeSet> => {
    if (changeSet && changeSet.status !== "published" && changeSet.status !== "abandoned") return changeSet;
    const created = await client.createChangeSet(projectId, title);
    setChangeSet(created);
    return created;
  }, [changeSet, client, projectId]);

  const previewOperations = useCallback(async (operations: AuthoringOperationInput[], base?: { diagramId: string; revision: number }) => {
    const target = await ensure(defaultTitle());
    const result = await run(() => client.previewAuthoring(projectId, target.changeSetId, { operations, ...(base ? { base } : {}) }));
    if (result) setPreview(result);
    return result;
  }, [client, ensure, projectId, run]);

  const applyOperations = useCallback(async (operations: AuthoringOperationInput[], base?: { diagramId: string; revision: number }) => {
    const target = await ensure(defaultTitle());
    const result = await run(() => client.applyAuthoring(projectId, target.changeSetId, { operations, ...(base ? { base } : {}) }));
    if (result) {
      setPreview(result);
      // The change set's counters and validation move with every apply.
      const refreshed = await client.getChangeSet(projectId, target.changeSetId).catch(() => undefined);
      if (refreshed) setChangeSet(refreshed);
    }
    return result;
  }, [client, ensure, projectId, run]);

  const transition = useCallback(async (action: (changeSetId: string) => Promise<ChangeSet>) => {
    if (!changeSet) return undefined;
    const result = await run(() => action(changeSet.changeSetId));
    if (result) setChangeSet(result);
    return result;
  }, [changeSet, run]);

  return {
    changeSet, busy, preview, conflict, error,
    ensure,
    adopt: setChangeSet,
    clearPreview: () => setPreview(undefined),
    clearError: () => { setError(undefined); setConflict(undefined); },
    previewOperations,
    applyOperations,
    submit: () => transition(id => client.submitForReview(projectId, id)),
    approve: () => transition(id => client.approve(projectId, id)),
    requestChanges: note => transition(id => client.requestChanges(projectId, id, note)),
    publish: message => transition(id => client.publish(projectId, id, message)),
    abandon: () => transition(id => client.abandon(projectId, id)),
  };
}

function defaultTitle(): string {
  return `Workbench edit ${new Date().toISOString().slice(0, 16).replace("T", " ")}`;
}

/**
 * §8.3 — which transitions the server will accept from a given state.
 *
 * Mirrored here to decide which buttons to ENABLE. The server is the authority
 * and refuses anything else; this table only avoids offering an action that is
 * certain to be refused.
 */
export const NEXT_ACTIONS: Record<string, string[]> = {
  draft: ["submit", "abandon"],
  validating: [],
  ready_for_review: ["approve", "requestChanges", "abandon"],
  changes_requested: ["submit", "abandon"],
  approved: ["publish", "abandon"],
  publishing: [],
  published: [],
  abandoned: [],
  conflicted: ["abandon"],
};

export function canDo(status: string | undefined, action: string): boolean {
  return status !== undefined && (NEXT_ACTIONS[status] ?? []).includes(action);
}
